import type { TenantRes } from "@agentcall/core/res"
import { cva, type VariantProps } from "class-variance-authority"
import type * as React from "react"
import { cn } from "@/lib/utils"

const tenantStatusBadgeVariants = cva(
  "inline-flex items-center rounded-full border border-transparent px-2 py-0.5 text-xs font-bold transition-colors",
  {
    variants: {
      variant: {
        occupied: "bg-[#354267] text-white",
        vacant: "bg-slate-200 text-[#9eafc7]",
      },
    },
    defaultVariants: {
      variant: "vacant",
    },
  }
)

const TENANT_STATUS_LABELS = {
  occupied: "入居中",
  vacant: "空室",
} as const

export type TenantStatusBadgeVariant = VariantProps<typeof tenantStatusBadgeVariants>["variant"]

export type TenantStatusBadgeInfo = {
  variant: NonNullable<TenantStatusBadgeVariant>
  label: string
}

export const getTenantStatusBadgeInfo = (
  tenant: TenantRes | null | undefined
): TenantStatusBadgeInfo => {
  if (tenant) {
    return { variant: "occupied", label: TENANT_STATUS_LABELS.occupied }
  }
  return { variant: "vacant", label: TENANT_STATUS_LABELS.vacant }
}

export interface TenantStatusBadgeProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children">,
    VariantProps<typeof tenantStatusBadgeVariants> {
  tenant: TenantRes | null | undefined
}

function TenantStatusBadge({ tenant, className, ...props }: TenantStatusBadgeProps) {
  const badgeInfo = getTenantStatusBadgeInfo(tenant)

  return (
    <div
      className={cn(tenantStatusBadgeVariants({ variant: badgeInfo.variant }), className)}
      {...props}
    >
      {badgeInfo.label}
    </div>
  )
}

export { TenantStatusBadge, tenantStatusBadgeVariants }
